// src/components/counseling/forms/TerminationForm.jsx
import React, { useState, useEffect } from 'react';
import { FormInput, FormSelect, FormTextArea } from '../../forms';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TerminationForm = ({ onSubmit, initialData = {} }) => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    terminationDate: new Date().toISOString().split('T')[0],
    reasonForTermination: '',
    goalsAchieved: [],
    goalsPartiallyAchieved: [],
    goalsNotAchieved: [],
    recommendationsForFuture: '',
    referralsMade: '',
    clientAgreement: false,
    counselorSignature: '',
    clientSignature: ''
  });

  // Handle initialData properly
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...initialData,
        goalsAchieved: initialData.goalsAchieved || [],
        goalsPartiallyAchieved: initialData.goalsPartiallyAchieved || [],
        goalsNotAchieved: initialData.goalsNotAchieved || []
      }));
    }
  }, [initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate form
      if (!formData.terminationDate || !formData.reasonForTermination ||
          !formData.recommendationsForFuture || !formData.counselorSignature ||
          !formData.clientSignature || !formData.clientAgreement) {
        throw new Error('Please fill in all required fields and confirm client agreement');
      }

      // Submit form
      const response = await onSubmit(formData);
          // Success alert if the submission was successful
    if (response?.data?.status === 'success') {
      alert("✅ Termination form submitted successfully. Redirecting...");
      navigate("/counselor/sessions");
    }
    } catch (err) {
      console.error('Termination form error:', err);
      setError(err.message || 'Failed to submit termination form');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAsDraft = async () => {
    try {
      setLoading(true);
      setError(null);
      await onSubmit({ ...formData, status: 'draft' });
    } catch (err) {
      setError(err.message || 'Failed to save draft');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Form fields remain the same but with updated state handling */}
        <FormInput
          type="date"
          label="Termination Date"
          value={formData.terminationDate}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            terminationDate: e.target.value
          }))}
          required
        />

        <FormSelect
          label="Reason for Termination"
          value={formData.reasonForTermination}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            reasonForTermination: e.target.value
          }))}
          options={[
            { value: 'goals-achieved', label: 'Goals Achieved' },
            { value: 'client-request', label: 'Client Request' },
            { value: 'referral', label: 'Referral to Other Services' },
            { value: 'completion', label: 'Natural Completion' },
            { value: 'other', label: 'Other' }
          ]}
          required
        />

        {/* Goals Assessment section */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Goals Assessment</h3>
          
          <FormTextArea
            label="Goals Achieved"
            value={formData.goalsAchieved.join('\n')}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              goalsAchieved: e.target.value.split('\n').filter(Boolean)
            }))}
            placeholder="Enter each achieved goal on a new line"
            rows={3}
          />

          <FormTextArea
            label="Goals Partially Achieved"
            value={formData.goalsPartiallyAchieved.join('\n')}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              goalsPartiallyAchieved: e.target.value.split('\n').filter(Boolean)
            }))}
            placeholder="Enter each partially achieved goal on a new line"
            rows={3}
          />

          <FormTextArea
            label="Goals Not Achieved"
            value={formData.goalsNotAchieved.join('\n')}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              goalsNotAchieved: e.target.value.split('\n').filter(Boolean)
            }))}
            placeholder="Enter each unachieved goal on a new line"
            rows={3}
          />
        </div>

        <FormTextArea
          label="Recommendations for Future"
          value={formData.recommendationsForFuture}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            recommendationsForFuture: e.target.value
          }))}
          rows={4}
          required
        />

        <FormTextArea
          label="Referrals Made"
          value={formData.referralsMade}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            referralsMade: e.target.value
          }))}
          rows={4}
        />

        {/* Client Agreement */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="clientAgreement"
            checked={formData.clientAgreement}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              clientAgreement: e.target.checked
            }))}
            className="h-4 w-4 text-uzima-maroon-500 rounded border-gray-300"
            required
          />
          <label htmlFor="clientAgreement" className="ml-2 text-sm text-gray-700">
            Client agrees to termination of counseling services
          </label>
        </div>

        {/* Signatures */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <FormInput
              label="Counselor Signature"
              value={formData.counselorSignature}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                counselorSignature: e.target.value
              }))}
              placeholder="Type full name to sign"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              By signing, I confirm that appropriate termination procedures have been followed.
            </p>
          </div>

          <div>
            <FormInput
              label="Client Signature"
              value={formData.clientSignature}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                clientSignature: e.target.value
              }))}
              placeholder="Type full name to sign"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              By signing, I acknowledge the termination of counseling services.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={handleSaveAsDraft}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Save as Draft
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-uzima-maroon-500 rounded-md hover:bg-uzima-maroon-600 disabled:bg-blue-300"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Complete Termination
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TerminationForm;